import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';

const PaystackCallback = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleCallback = () => {
      const reference = searchParams.get('reference');
      const trxref = searchParams.get('trxref');
      const status = searchParams.get('status');
      
      // Use reference from URL parameters
      const paymentReference = reference || trxref;
      
      // Check for cancellation or failure
      if (status === 'cancelled' || status === 'failed') {
        console.log('Payment cancelled or failed by user');
        setTimeout(() => {
          const mobileErrorUrl = 'yarngpt://payment/error?message=Payment was cancelled or failed';
          
          // Direct redirect without fallback to prevent error page flash
          window.location.href = mobileErrorUrl;
          
          // Close the window after attempting redirect
          setTimeout(() => {
            try {
              window.close();
            } catch (e) {
              console.log('Cannot close window:', e);
            }
          }, 1000);
        }, 2000);
        return;
      }
      
      if (!paymentReference) {
        console.log('No payment reference found');
        setTimeout(() => {
          const mobileErrorUrl = 'yarngpt://payment/error?message=Invalid payment reference';
          
          // Direct redirect without fallback to prevent error page flash
          window.location.href = mobileErrorUrl;
          
          // Close the window after attempting redirect
          setTimeout(() => {
            try {
              window.close();
            } catch (e) {
              console.log('Cannot close window:', e);
            }
          }, 1000);
        }, 2000);
        return;
      }

      console.log('Payment completed for reference:', paymentReference);
      console.log('Webhook processes payment server-side, redirecting to app...');
      
      // Show brief success message then redirect to app
      setTimeout(() => {
        // Try to open the mobile app - but don't fallback to web as it causes error flash
        const mobileAppUrl = 'yarngpt://payment/success';
        
        // Direct redirect without fallback to prevent error page flash
        window.location.href = mobileAppUrl;
        
        // Close the window after attempting redirect (for WebView)
        setTimeout(() => {
          try {
            window.close();
          } catch (e) {
            console.log('Cannot close window:', e);
          }
        }, 1000);
      }, 2000);
    };

    handleCallback();
  }, [searchParams]);

  // Check if this is an error case
  const status = searchParams.get('status');
  const isError = status === 'cancelled' || status === 'failed' || (!searchParams.get('reference') && !searchParams.get('trxref'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isError 
          ? 'linear-gradient(135deg, #f56565 0%, #ef4444 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={10} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            {isError ? (
              <>
                <Error sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom color="error.main" fontWeight="bold">
                  Payment {status === 'cancelled' ? 'Cancelled' : 'Failed'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  {status === 'cancelled' 
                    ? 'You cancelled the payment. No charges were made.'
                    : 'Payment could not be processed. Please try again.'
                  }
                </Typography>
              </>
            ) : (
              <>
                <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom color="success.main" fontWeight="bold">
                  Payment Successful! 🎉
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  Your payment has been confirmed. Redirecting you back to YarnGPT...
                </Typography>
              </>
            )}
            <CircularProgress size={30} sx={{ color: isError ? 'error.main' : 'success.main' }} />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PaystackCallback; 